import { zodResolver } from '@hookform/resolvers/zod';
import { Card, CardContent, CardHeader, Skeleton } from '@ibsys/kit';
import { FC, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  useCreateEmployeeGroup,
  useDeleteEmployeeGroup,
  useEmployeeGroup,
  useUpdateEmployeeGroup,
} from '@/entities/employeeGroups';
import { useAppNavigate } from '@/shared/lib';
import { EmployeeGroupForm, EmployeeGroupFormValues, useEmployeeGroupSchema } from '../EmployeeGroupForm';

type Props = {
  groupId?: number;
  isCreating: boolean;
  onUnsavedChange?: (hasUnsaved: boolean) => void;
};

/**
 * Контейнер карточки группы операторов: загрузка/мутации, инициализация формы,
 * проксирование событий сохранения/удаления в API.
 */
export const EmployeeGroupsCard: FC<Props> = ({ groupId, isCreating, onUnsavedChange }) => {
  const navigate = useAppNavigate();
  const schema = useEmployeeGroupSchema();

  const { data, isLoading } = useEmployeeGroup(!isCreating ? groupId : undefined);

  const defaults: EmployeeGroupFormValues = useMemo(
    () => ({
      name: data?.name ?? '',
      description: data?.description ?? '',
      externalId: data?.externalId ?? '',
    }),
    [data],
  );

  const form = useForm<EmployeeGroupFormValues>({
    defaultValues: defaults,
    resolver: zodResolver(schema),
  });
  const { formState } = form;

  useEffect(() => {
    onUnsavedChange?.(formState.isDirty);
  }, [formState.isDirty, onUnsavedChange]);

  useEffect(() => {
    form.reset(isCreating ? { name: '', description: '', externalId: '' } : defaults);
  }, [form, defaults, isCreating]);

  const { mutateAsync: createGroup, isPending: isCreatingPending } = useCreateEmployeeGroup();
  const { mutateAsync: updateGroup, isPending: isUpdatingPending } = useUpdateEmployeeGroup();
  const { mutateAsync: deleteGroup } = useDeleteEmployeeGroup();

  const isLoadingSaving = isCreatingPending || isUpdatingPending;

  const handleSave = async (values: EmployeeGroupFormValues) => {
    const payload = {
      name: values.name,
      description: values.description?.trim() ? values.description : null,
      externalId: values.externalId?.trim() ? values.externalId : null,
    };

    if (isCreating) {
      const created = await createGroup(payload);
      form.reset(values);
      navigate('/employee-groups/:id', { id: created.id.toString() });
    } else if (groupId) {
      await updateGroup({ id: groupId, body: payload });
      form.reset(values);
    }
  };

  const handleUpdateName = async (name: string) => {
    if (!groupId || !data) return;
    await updateGroup({
      id: groupId,
      body: {
        name,
        description: data.description ?? null,
        externalId: data.externalId ?? null,
      },
    });
  };

  const handleDelete = async () => {
    if (!groupId) return;
    await deleteGroup(groupId);
    form.reset({ name: '', description: '', externalId: '' });
    navigate('/employee-groups');
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="w-45 h-8" />
        </CardHeader>
        <CardContent>
          <Skeleton className="w-45 h-8" />
        </CardContent>
      </Card>
    );
  }

  return (
    <FormProvider {...form}>
      <EmployeeGroupForm
        key={`${isCreating}-${groupId}`}
        isCreating={isCreating}
        groupId={isCreating ? undefined : groupId}
        isLoadingSaving={isLoadingSaving}
        onSave={handleSave}
        onDelete={handleDelete}
        onUpdateName={handleUpdateName}
      />
    </FormProvider>
  );
};
